/**
 * useRotationPlayer — state machine for stepping through a saved cast
 * rotation (a sequence of `{abilityId, delayMs}` steps).
 *
 * Owns: idle / playing / paused state, current step index, timer
 * scheduling, optional looping. Caller supplies the `castFn` that
 * actually performs the cast — the hook stays decoupled from
 * ServiceRegistry / EntitySystem so it's testable in isolation.
 *
 * Used by AbilityTestRig (Panel 3 in the ability-debug family). DJ
 * records or hand-builds rotations, presses play, watches the
 * AbilityLifecycleMonitor confirm each step fires.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface RotationStep {
  abilityId: string;
  /** Milliseconds to wait BEFORE this step fires. 0 = fire immediately. */
  delayMs: number;
}

export interface RotationDefinition {
  id: string;
  name: string;
  steps: RotationStep[];
}

export type RotationStatus = 'idle' | 'playing' | 'paused' | 'completed';

export interface UseRotationPlayerOptions {
  /** Loop forever? When true, restarts at step 0 on completion. */
  loop?: boolean;
  /** Callback to actually perform a cast. Called once per step. */
  castFn: (abilityId: string) => void;
  /** Optional: called on every status change (for telemetry / UI). */
  onStatusChange?: (status: RotationStatus) => void;
}

export interface UseRotationPlayerResult {
  status: RotationStatus;
  currentStep: number;
  /** Start playback from step 0, or from `pausedAtStep` if resuming. */
  play: (rotation: RotationDefinition) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

export function useRotationPlayer(opts: UseRotationPlayerOptions): UseRotationPlayerResult {
  const [status, setStatus] = useState<RotationStatus>('idle');
  const [currentStep, setCurrentStep] = useState<number>(-1);

  // Refs survive React re-renders — the timer + active rotation
  // shouldn't reset when the parent re-renders mid-playback.
  const rotationRef = useRef<RotationDefinition | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepIndexRef = useRef<number>(-1);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const setStatusAndNotify = useCallback((next: RotationStatus) => {
    setStatus(next);
    optsRef.current.onStatusChange?.(next);
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleStep = useCallback((index: number) => {
    const rotation = rotationRef.current;
    if (!rotation) return;
    if (index >= rotation.steps.length) {
      // End of rotation.
      if (optsRef.current.loop) {
        scheduleStep(0);
        return;
      }
      stepIndexRef.current = -1;
      setCurrentStep(-1);
      setStatusAndNotify('completed');
      return;
    }

    const step = rotation.steps[index];
    timerRef.current = setTimeout(() => {
      stepIndexRef.current = index;
      setCurrentStep(index);
      try {
        optsRef.current.castFn(step.abilityId);
      } catch {
        // Cast errors swallowed — the rotation continues. The
        // AbilityLifecycleMonitor will surface the failure.
      }
      scheduleStep(index + 1);
    }, Math.max(0, step.delayMs));
  }, [setStatusAndNotify]);

  const play = useCallback((rotation: RotationDefinition) => {
    clearTimer();
    rotationRef.current = rotation;
    stepIndexRef.current = -1;
    setCurrentStep(-1);
    setStatusAndNotify('playing');
    scheduleStep(0);
  }, [clearTimer, scheduleStep, setStatusAndNotify]);

  const pause = useCallback(() => {
    if (status !== 'playing') return;
    clearTimer();
    setStatusAndNotify('paused');
  }, [status, clearTimer, setStatusAndNotify]);

  const resume = useCallback(() => {
    if (status !== 'paused') return;
    setStatusAndNotify('playing');
    // Resume from the step AFTER the last fired one.
    scheduleStep(stepIndexRef.current + 1);
  }, [status, scheduleStep, setStatusAndNotify]);

  const stop = useCallback(() => {
    clearTimer();
    rotationRef.current = null;
    stepIndexRef.current = -1;
    setCurrentStep(-1);
    setStatusAndNotify('idle');
  }, [clearTimer, setStatusAndNotify]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return { status, currentStep, play, pause, resume, stop };
}

// ── LocalStorage persistence ──────────────────────────────────

const STORAGE_KEY = 'helix2000.abilityTestRig.rotations';

export function loadRotations(): RotationDefinition[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r) =>
        r && typeof r.id === 'string' && typeof r.name === 'string' && Array.isArray(r.steps),
    );
  } catch {
    return [];
  }
}

export function saveRotations(rotations: RotationDefinition[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rotations));
  } catch {
    // Storage quota / private mode — silent fail. The in-memory state
    // is still correct; just won't survive reload.
  }
}
